import { useGetAllMilestonesQuery } from 'api/siteApi';
import { addDays, isPast } from 'date-fns';
import {
  Calendar,
  CalendarCheck2,
  CircleCheck,
  Clock,
  ClockAlert,
  Timer,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { Skeleton } from 'ui';
import Badge from 'ui/components/Badge';
import Button from 'ui/components/Button';
import DropdownMenu from 'ui/components/Dropdown';
import Label from 'ui/components/Label';
import { getIsBottomBarLoading } from '../../../store/uiSlice';

interface Props {
  siteId?: string | null;
}

export const ProjectStages = ({ siteId }: Props) => {

  const { data } = useGetAllMilestonesQuery({ siteId: siteId ?? '' }, { skip: !siteId });
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);
  const getTimeMessage = (timeLeft: number, dueDate: string) => {
    // Check if the due date is in the past
    const isPastDue = isPast(new Date(dueDate));
    let absTimeRemaining = Math.abs(timeLeft);
    const units = [
      { unit: "seconds", value: 60 },
      { unit: "minutes", value: 60 },
      { unit: "hours", value: 24 },
      { unit: "days", value: Infinity }
    ];

    for (let i = 0; i < units.length; i++) {
      if (absTimeRemaining < units[i].value) {
        const unitValue = Math.floor(absTimeRemaining);
        const unitName = unitValue === 1 ? units[i].unit.slice(0, -1) : units[i].unit;

        return isPastDue
          ? `${unitValue} ${unitName} overdue`
          : `${unitValue} ${unitName} remaining`;
      }
      absTimeRemaining = absTimeRemaining / units[i].value;
    }
  }

  const getMilestoneVariant = (dueDate: string, status: number) => {
    if (status === 1) {
      return "primary";    // Green - completed
    }

    if (isPast(new Date(dueDate))) {
      return "destructive"; // Red - overdue
    }

    const twoDaysFromNow = addDays(new Date(), 2);
    const dueDateObj = new Date(dueDate);

    if (dueDateObj <= twoDaysFromNow) {
      return "warning";    // Orange - due soon
    }

    return "tertiary";   // Purple - default
  };

  const getClockIcon = (dueDate: string, status: number) => {
    if (status === 1) {
      return <CircleCheck size={18} className='text-ata-green' />;
    }

    if (isPast(new Date(dueDate))) {
      return <ClockAlert size={18} className='text-destructive' />;
    }

    return <Clock size={18} />;
  };

  return (
    <>
      {isBottomBarLoading ? (
        <Skeleton className="h-9 w-9 rounded-md" />
      ) : (
        <>
          <DropdownMenu modal={false}>
            <DropdownMenu.Trigger asChild>
              <Button
                size="icon"
                variant="outline"
                leftIcon={<Timer />}
                tooltip="Track project stages and deadlines"
                tooltipSide="top"
                className='hidden md:flex'
              ></Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="z-10" portal={false}>
              <DropdownMenu.Label>Project Stages and Deadlines</DropdownMenu.Label>
              <DropdownMenu.Separator />
              {(data && data.result.filter(d => d.status !== 1).length > 0) && (
                <DropdownMenu.Item className="flex flex-col items-start gap-1 focus:bg-transparent text-ata-grey focus:text-ata-grey cursor-default">
                  <Label className="text-xs">Active Stage</Label>
                  {data.result.filter(d => d.status !== 1).map(d => (
                    <div className="flex w-full justify-between gap-4 rounded-lg border border-[#E4E4E7] p-2 cursor-default">
                      <div className="flex gap-1">
                        {getClockIcon(d.due_date_at, d.status)} <span>{d.title}</span>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={getMilestoneVariant(d.due_date_at, d.status)}>
                          {getTimeMessage(d.time_left, d.due_date_at)}
                        </Badge>
                        <span className="flex gap-1">
                          <Calendar size={18} />
                          <span> Due {new Date(`${d.due_date_at}T${d.due_time_at}Z`).toLocaleDateString("en-us", { month: "short", day: "numeric", year: "numeric" })}</span>
                        </span>
                      </div>
                    </div>)
                  )}
                </DropdownMenu.Item>
              )}
              {(data && data.result.filter(d => d.status === 1).length > 0) && (
                <DropdownMenu.Item className="flex flex-col items-start gap-1 focus:bg-transparent text-ata-grey focus:text-ata-grey cursor-default">
                  <Label className="text-xs">Completed Stages</Label>
                  {data.result.filter(d => d.status === 1).map(d => (
                    <div className="text-primary border-primary flex w-full justify-between gap-4 rounded-lg border p-2 cursor-default">
                      <div className="flex gap-1">
                        <CircleCheck size={18} /> <span>{d.title}</span>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="primary">Complete</Badge>
                        <span className="flex gap-1">
                          <CalendarCheck2 size={18} />
                          <span className="line-through">Due {new Date(`${d.due_date_at}T${d.due_time_at}Z`).toLocaleDateString("en-us", { month: "short", day: "numeric", year: "numeric" })}</span>
                        </span>
                      </div>
                    </div>)
                  )}
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        </>
      )
      }
    </>
  );
};